import { CLOUD_FRONT } from '../configurations/api/url';
import type { ICampaignThreadAttachment } from '../redux/buzzbridge/campaignThreadMessage.types';
import type { IMessageAttachment } from '../redux/buzzbridge/campaignWorkspace.types';

export type CampaignThreadAttachmentKind = ICampaignThreadAttachment['kind'];

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
const VIDEO_EXT = /\.(mp4|webm|mov|m4v|mkv)(\?|$)/i;
const AUDIO_EXT = /\.(mp3|wav|ogg|aac|m4a|flac)(\?|$)/i;

export function resolveCampaignMediaUrl(raw?: string | null): string {
    const value = (raw ?? '').trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    const key = value.replace(/^\//, '');
    return `${CLOUD_FRONT}${key}`;
}

function inferKindFromUrl(url: string): CampaignThreadAttachmentKind {
    if (IMAGE_EXT.test(url)) return 'image';
    if (VIDEO_EXT.test(url)) return 'video';
    if (AUDIO_EXT.test(url)) return 'audio';
    return 'document';
}

function inferKindFromMime(mime?: string): CampaignThreadAttachmentKind | null {
    if (!mime) return null;
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
}

/** Normalize server attachment object or legacy bare string key/url. */
export function normalizeThreadAttachment(raw: unknown, index = 0): IMessageAttachment | null {
    if (raw == null) return null;

    if (typeof raw === 'string') {
        const url = resolveCampaignMediaUrl(raw);
        const kind = inferKindFromUrl(url || raw);
        return {
            id: `legacy-${index}-${raw}`,
            kind,
            url,
            processing_complete: Boolean(url),
        };
    }

    if (typeof raw !== 'object') return null;

    const att = raw as ICampaignThreadAttachment;
    const kind = att.kind ?? inferKindFromMime(att.mime_type) ?? inferKindFromUrl(att.url ?? '');
    const isLocation = kind === 'location';
    const url = isLocation ? '' : att.url ? resolveCampaignMediaUrl(att.url) : '';
    const downloadUrl = att.download_url ? resolveCampaignMediaUrl(att.download_url) : undefined;
    const thumbnailUrl = att.thumbnail_url ? resolveCampaignMediaUrl(att.thumbnail_url) : undefined;
    const watchUrl = att.watch_url?.trim() || undefined;
    const hls = att.hls ? resolveCampaignMediaUrl(att.hls) : undefined;

    return {
        id: att.id ?? `att-${index}`,
        kind,
        url,
        download_url: downloadUrl,
        mime_type: att.mime_type,
        filename: att.filename ?? att.label,
        byte_size: att.byte_size,
        thumbnail_url: thumbnailUrl,
        project_id: att.project_id,
        watch_url: watchUrl,
        hls,
        latitude: att.latitude ?? att.lat,
        longitude: att.longitude ?? att.lng,
        lat: att.lat ?? att.latitude,
        lng: att.lng ?? att.longitude,
        label: att.label,
        maps_url: att.maps_url,
        processing_complete: isLocation ? true : att.processing_complete !== false,
        processing_progress: att.processing_progress,
        processing_error: att.processing_error,
    };
}

export function normalizeThreadAttachments(raw?: unknown[]): IMessageAttachment[] {
    if (!raw?.length) return [];
    return raw
        .map((item, index) => normalizeThreadAttachment(item, index))
        .filter((item): item is IMessageAttachment => item != null);
}

/** Best URL for inline playback (video/audio) or preview (image). */
export function attachmentPlaybackUrl(att: IMessageAttachment): string {
    if (att.kind === 'link') return att.watch_url ?? att.url;
    if (att.kind === 'video') {
        if (att.processing_complete) return att.hls ?? att.url ?? att.watch_url ?? '';
        return att.thumbnail_url ?? att.url ?? '';
    }
    if (att.kind === 'image') return att.thumbnail_url ?? att.url;
    return att.url || att.download_url || att.watch_url || '';
}

export function attachmentOpenUrl(att: IMessageAttachment): string {
    if (att.kind === 'link' || att.kind === 'video') return att.watch_url ?? att.url;
    return att.download_url ?? att.url;
}

export function fileToAttachmentKind(file: File): 'image' | 'audio' | 'document' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
}
